import React, { useState } from "react";
import { Switch } from "react-router-dom";
import { observer } from "mobx-react";
import { SearchPage } from "@features/offer/desktop/pages/search";
import { AsideWidgets } from "@features/side-widgets";
import { ErrorPage } from "@components-shared/desktop/error";
import { PrematchOfferMenu } from "@features/offer/desktop/offer-menu";
import { NoMatchRoute, RouteElement } from "@components/navigation";
import { useRootAppStore } from "@hooks";
import classNames from "classnames";
import { AdditionalOfferContainerRefContext } from "@state/context/additional-offer/AdditionalOfferContainerRefContext";
import { SetIsAdditionalOfferOpenCtxProviderForParentLayout } from "@state/context/additional-offer/SetIsAdditionalOfferOpenCtxProviderForParentLayout";
import PrematchOfferMenuStore from "@state/stores/offer/prematch/PrematchOfferMenuStore";
import { useFavoritesCounter } from "@state/hooks/offer/useFavoritesCounter";
import { LiveStatus } from "@gp/models";
import { AdditionalOffer } from "@features/offer/desktop/additional-offer";
import { HeaderType } from "@data-types/layout/headerTypeEnum";
import DesktopLayout from "@components-shared/desktop/layout/DesktopLayout";

export default observer(function SearchRouteLayout(props: {
	menuStore: PrematchOfferMenuStore;
}) {
	const [additionalOfferContainerRef, setAdditionalOfferContainerRef] =
		useState<HTMLDivElement | null>(null);
	const rootStore = useRootAppStore();
	const { isSideMenuExpanded: isMenuExpanded } = rootStore;

	const [isAdditionalOfferVisible, setIsAdditionalOfferOpen] =
		React.useState(false);

	useFavoritesCounter(LiveStatus.PREMATCH);

	//#region css classes

	const mainClassNames = classNames("l--main l--main--offer l--search", {
		"sidebar-left-expanded": isMenuExpanded,
		"sidebar-left-collapsed": !isMenuExpanded,
	});

	const asideClasses = classNames("l--aside--left", {
		"is-expanded": isMenuExpanded,
	});

	const contentClassNames = classNames("l--content", {
		"additional-is-visible": isAdditionalOfferVisible,
		"additional-is-hidden": !isAdditionalOfferVisible,

		"sidebar-left-expanded": isMenuExpanded,
		"sidebar-left-collapsed": !isMenuExpanded,
	});

	const additionalOfferClassNames = classNames("l--content__additional", {
		"is-visible": isAdditionalOfferVisible,
		"is-hidden": !isAdditionalOfferVisible,

		"sidebar-left-expanded": isMenuExpanded,
		"sidebar-left-collapsed": !isMenuExpanded,
	});

	const footerClassNames = classNames("l--footer", {
		"sidebar-left-expanded": isMenuExpanded,
		"sidebar-left-collapsed": !isMenuExpanded,
	});

	//#endregion css classes

	return (
		<SetIsAdditionalOfferOpenCtxProviderForParentLayout.Provider
			value={{
				setIsAdditionalOpen: setIsAdditionalOfferOpen,
				isAdditionalOpen: isAdditionalOfferVisible,
			}}
		>
			<DesktopLayout
				headerType={HeaderType.WITH_SIDEBAR}
				mainClassName={mainClassNames}
				footerClassName={footerClassNames}
			>
				<aside className={asideClasses}>
					<PrematchOfferMenu
						prematchOfferMenuStore={props.menuStore}
					/>
				</aside>

				<section className={contentClassNames}>
					<ErrorPage>
						<Switch>
							<RouteElement
								exact
								path="/:culture/search/:period/:searchTerm"
							>
								<AdditionalOfferContainerRefContext.Provider
									value={additionalOfferContainerRef}
								>
									<SearchPage />
									<div
										id="additional-offer-container"
										ref={(newRef: HTMLDivElement | null) =>
											setAdditionalOfferContainerRef(
												newRef
											)
										}
										className={additionalOfferClassNames}
									/>
									<AdditionalOffer />
								</AdditionalOfferContainerRefContext.Provider>
							</RouteElement>
							<NoMatchRoute />
						</Switch>
					</ErrorPage>
				</section>

				<AsideWidgets />
			</DesktopLayout>
		</SetIsAdditionalOfferOpenCtxProviderForParentLayout.Provider>
	);
});
